// Ordered roles also retain the six-color format used by saved/shared scenes.
export const FRACTAL_COLOR_ROLES = ["Highlights", "Edges", "Branches", "Depth", "Background", "Interior"] as const;

export const FRACTAL_PALETTES = [
  { id: "copper", name: "Obsidian & Copper", colors: ["#fff0c9", "#f3b76c", "#d87543", "#135563", "#092b36", "#070b12"] },
  { id: "pearl", name: "Pearl & Ink", colors: ["#fff8ed", "#d89074", "#164f61", "#67a5ad", "#f0e7da", "#261c36"] },
  { id: "aurora", name: "Aurora", colors: ["#dcfff4", "#8a61d2", "#27d9c5", "#173965", "#11132d", "#100b1c"] },
  { id: "ember", name: "Ember", colors: ["#fff0cc", "#ffb36b", "#e76354", "#583253", "#201727", "#0a0810"] },
  { id: "lagoon", name: "Lagoon", colors: ["#f4ffcf", "#d3e78b", "#47c8af", "#175664", "#082937", "#050e19"] },
  { id: "porcelain", name: "Porcelain", colors: ["#fff5dd", "#c38245", "#43305f", "#a28dab", "#e9e4f1", "#1c172e"] },
] as const;

function rgb(hex: string) {
  return [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16) / 255);
}

function luminance(hex: string) {
  const [r, g, b] = rgb(hex).map((value) => value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);
  return r * 0.2126 + g * 0.7152 + b * 0.0722;
}

// Rotate the whole palette together without importing the renderer into the UI.
function shiftHue(hex: string, shift: number) {
  const [r, g, b] = rgb(hex);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  if (delta === 0) return hex;
  const lightness = (max + min) / 2;
  const saturation = delta / (1 - Math.abs(2 * lightness - 1));
  const sector = max === r ? (g - b) / delta : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4;
  const hue = ((sector / 6 + shift) % 1 + 1) % 1;
  const amplitude = saturation * Math.min(lightness, 1 - lightness);
  const channels = [0, 8, 4].map((offset) => {
    const k = (offset + hue * 12) % 12;
    return lightness - amplitude * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  });
  return `#${channels.map((value) => Math.round(value * 255).toString(16).padStart(2, "0")).join("")}`;
}

export function fractalPaletteContrast(colors: readonly string[]) {
  const branch = luminance(colors[2]);
  const background = luminance(colors[4]);
  return (Math.max(branch, background) + 0.05) / (Math.min(branch, background) + 0.05);
}

/** Choose a different color direction, then vary its hues as a group. */
export function randomFractalColors(current?: readonly string[]): string[] {
  const distance = (colors: readonly string[]) => colors.reduce((sum, hex, index) => {
    const a = rgb(hex);
    const b = rgb(current?.[index] ?? "#000000");
    return sum + a.reduce((distance, channel, index) => distance + (channel - b[index]) ** 2, 0);
  }, 0);
  const closest = current ? FRACTAL_PALETTES.reduce((a, b) => distance(a.colors) < distance(b.colors) ? a : b) : null;
  const choices = FRACTAL_PALETTES.filter((palette) => palette !== closest);
  const palette = choices[Math.floor(Math.random() * choices.length)];
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const hueShift = (Math.random() - 0.5) * 0.06;
    const colors = palette.colors.map((hex) => shiftHue(hex, hueShift));
    if (fractalPaletteContrast(colors) >= 3) return colors;
  }
  return [...palette.colors];
}
